/**
 * Datos de módulos específicos por cada destino.
 * Cada ciudad tiene: cityData (BeforeYouTravel), cityPrices (BudgetCalculator),
 * checklistData (TravelChecklist).
 *
 * Se busca por guideId (tokio, kioto, paris, londres, ny, seul, sanFrancisco).
 */

const COLORS = {
	tokio: { accent: "#EF4444", light: "rgba(239, 68, 68, 0.1)" },
	kioto: { accent: "#F43F5E", light: "rgba(244, 63, 94, 0.1)" },
	paris: { accent: "#6366F1", light: "rgba(99, 102, 241, 0.1)" },
	londres: { accent: "#3B82F6", light: "rgba(59, 130, 246, 0.1)" },
	ny: { accent: "#8B5CF6", light: "rgba(139, 92, 246, 0.1)" },
	seul: { accent: "#EC4899", light: "rgba(236, 72, 153, 0.1)" },
	sanFrancisco: { accent: "#F59E0B", light: "rgba(245, 158, 11, 0.1)" },
};

// ─────────────────────────────────────────────
// TOKIO
// ─────────────────────────────────────────────
const tokioCityData = {
	cityId: "tokio",
	cityName: "Tokio",
	accentColor: COLORS.tokio.accent,
	accentColorLight: COLORS.tokio.light,
	documentation: {
		visa: "Con pasaporte español no necesitas visado hasta 90 días. Si tu pasaporte es de otro país, revisa si necesitas visado.",
		insurance:
			"Recomendado. La sanidad japonesa es excelente pero muy cara sin seguro.",
		vaccinations:
			"No hay vacunas obligatorias. Las básicas al día son suficientes.",
	},
	packing: [
		{ icon: "👟", item: "Zapatillas cómodas", note: "Caminarás mucho" },
		{
			icon: "🧦",
			item: "Calcetines limpios extra",
			note: "Te descalzas en templos",
		},
		{
			icon: "🔌",
			item: "Adaptador tipo A/B",
			note: "Clavijas planas americanas",
		},
		{ icon: "☂️", item: "Paraguas plegable", note: "Lluvias frecuentes" },
		{ icon: "👕", item: "Ropa en capas", note: "Aire acondicionado fuerte" },
		{ icon: "🧴", item: "Protector solar", note: "Verano intenso" },
	],
	apps: [
		{ name: "Suica/Pasmo (mobile)", description: "Tarjeta de transporte" },
		{ name: "Google Translate", description: "Cámara para traducir kanji" },
		{ name: "Navitime Japan", description: "Rutas de metro/tren" },
		{ name: "Tabelog", description: "Reseñas de restaurantes" },
		{ name: "Maps.me", description: "Mapas offline" },
	],
	connectivity: {
		sim: "Pocket WiFi en aeropuerto (~5€/día). SIM de datos desde 1.500¥.",
		esim: "eSIM con Ubigi, Airalo o Holafly. Muy cómodo.",
		wifi: "WiFi gratuito en estaciones, konbini y hoteles.",
	},
	money: {
		currency: "Yen japonés (¥)",
		exchangeRate: "1€ ≈ 160¥ (consulta antes de viajar)",
		cards: "Cada vez más aceptadas, pero muchos sitios son solo efectivo.",
		atm: "Cajeros en 7-Eleven y correos aceptan tarjetas extranjeras.",
		tip: "No se deja propina en Japón. Se considera de mala educación.",
	},
	language: {
		main: "Japonés",
		english: "Limitado. Señalización en estaciones en inglés.",
		phrases: [
			{ phrase: "Konnichiwa", meaning: "Hola / Buenas tardes" },
			{ phrase: "Arigatou gozaimasu", meaning: "Muchas gracias" },
			{ phrase: "Sumimasen", meaning: "Disculpe / Perdón" },
			{ phrase: "Ikura desu ka?", meaning: "¿Cuánto cuesta?" },
		],
	},
	weather: {
		description:
			"Cuatro estaciones bien marcadas. Veranos calurosos y húmedos, inviernos suaves.",
		bestTime: "Marzo-mayo (sakura) y octubre-noviembre (otoño).",
		avoid: "Junio-julio: temporada de lluvias (tsuyu).",
	},
};

const tokioCityPrices = {
	cityName: "Tokio",
	currency: "€",
	accentColor: COLORS.tokio.accent,
	accentColorLight: COLORS.tokio.light,
	accommodation: {
		budget: { min: 25, max: 50, label: "Hostel / Cápsula" },
		mid: { min: 60, max: 120, label: "Hotel business / Ryokan" },
		luxury: { min: 150, max: 400, label: "Hotel 5 estrellas" },
	},
	food: {
		budget: { min: 10, max: 20, label: "Ramen, gyudon, konbini" },
		mid: { min: 25, max: 45, label: "Restaurantes variados" },
		luxury: { min: 60, max: 150, label: "Sushi omakase, kaiseki" },
	},
	transport: {
		budget: { min: 5, max: 10, label: "Metro con Suica/Pasmo" },
		mid: { min: 10, max: 20, label: "Metro + algún taxi" },
		luxury: { min: 25, max: 50, label: "Taxi frecuente" },
	},
	activities: {
		budget: { min: 5, max: 15, label: "Templos gratuitos, paseos" },
		mid: { min: 15, max: 35, label: "Museos, TeamLab, tours" },
		luxury: { min: 40, max: 80, label: "Experiencias premium" },
	},
	fixedCosts: {
		flight: { min: 500, max: 900, label: "Vuelo ida/vuelta (aprox)" },
		insurance: { min: 25, max: 50, label: "Seguro de viaje" },
		sim: { min: 10, max: 25, label: "Pocket WiFi o eSIM" },
	},
	priceExamples: [
		{ item: "Ramen en local", price: "6-9€" },
		{ item: "Onigiri (konbini)", price: "1€" },
		{ item: "Billete metro", price: "1-2€" },
		{ item: "Entrada TeamLab", price: "20€" },
		{ item: "Cerveza en izakaya", price: "3-4€" },
		{ item: "Sushi conveyor belt", price: "8-12€" },
		{ item: "Agua en konbini", price: "0.60€" },
	],
};

const tokioChecklistData = {
	cityId: "tokio",
	cityName: "Tokio",
	accentColor: COLORS.tokio.accent,
	accentColorLight: COLORS.tokio.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "passport", text: "Pasaporte vigente" },
				{ id: "insurance", text: "Seguro de viaje contratado" },
				{ id: "copies", text: "Copias de documentos en la nube" },
				{ id: "jr-pass", text: "JR Pass (si aplica)" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelos reservados" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "teamlab", text: "Entrada TeamLab (se agotan)" },
				{ id: "tours", text: "Excursiones reservadas" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "wifi", text: "Pocket WiFi o eSIM" },
				{ id: "apps", text: "Apps descargadas (Navitime, Translate)" },
				{ id: "offline", text: "Mapas offline descargados" },
				{ id: "charger", text: "Cargador + adaptador tipo A/B" },
				{ id: "suica", text: "Suica/Pasmo configurada" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{
					id: "cash",
					text: "Yenes en efectivo (muchos sitios no aceptan tarjeta)",
				},
				{ id: "cards", text: "Tarjeta sin comisiones internacionales" },
				{ id: "notify-bank", text: "Avisar al banco del viaje" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "shoes", text: "Zapatillas cómodas para caminar" },
				{ id: "socks", text: "Calcetines extra (templos)" },
				{ id: "umbrella", text: "Paraguas plegable" },
				{ id: "adapter", text: "Adaptador enchufe tipo A/B" },
				{ id: "layers", text: "Ropa en capas" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online del vuelo" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// ─────────────────────────────────────────────
// KIOTO
// ─────────────────────────────────────────────
const kiotoCityData = {
	cityId: "kioto",
	cityName: "Kioto",
	accentColor: COLORS.kioto.accent,
	accentColorLight: COLORS.kioto.light,
	documentation: {
		visa: "Con pasaporte español no necesitas visado hasta 90 días. Si tu pasaporte es de otro país, revisa si necesitas visado.",
		insurance: "Recomendado. Sanidad japonesa excelente pero cara sin seguro.",
		vaccinations: "No hay vacunas obligatorias.",
	},
	packing: [
		{ icon: "👟", item: "Zapatillas cómodas", note: "Mucho camino en templos" },
		{ icon: "🧦", item: "Calcetines limpios", note: "Te descalzas mucho" },
		{ icon: "👖", item: "Ropa cómoda y modesta", note: "Respeto en templos" },
		{ icon: "🔌", item: "Adaptador tipo A/B", note: "Clavijas planas" },
		{ icon: "☂️", item: "Paraguas plegable", note: "Lluvias frecuentes" },
		{ icon: "📷", item: "Cámara de fotos", note: "Paisajes increíbles" },
	],
	apps: [
		{ name: "Navitime Japan", description: "Rutas de bus y tren" },
		{ name: "Google Translate", description: "Cámara para traducir" },
		{ name: "Maps.me", description: "Mapas offline" },
		{ name: "Tabelog", description: "Restaurantes locales" },
	],
	connectivity: {
		sim: "Misma SIM/WiFi que uses en Tokio funciona en todo Japón.",
		esim: "eSIM con Airalo o Holafly. Cobertura nacional.",
		wifi: "WiFi en estaciones, konbini y hoteles.",
	},
	money: {
		currency: "Yen japonés (¥)",
		exchangeRate: "1€ ≈ 160¥ (consulta antes de viajar)",
		cards: "Más sitios solo efectivo que en Tokio.",
		atm: "Cajeros en 7-Eleven y correos.",
		tip: "No se deja propina en Japón.",
	},
	language: {
		main: "Japonés",
		english:
			"Menos inglés que en Tokio. Señalización bilingüe en zonas turísticas.",
		phrases: [
			{ phrase: "Konnichiwa", meaning: "Hola / Buenas tardes" },
			{ phrase: "Arigatou gozaimasu", meaning: "Muchas gracias" },
			{ phrase: "Sumimasen", meaning: "Disculpe / Perdón" },
			{ phrase: "Oishii!", meaning: "¡Delicioso!" },
		],
	},
	weather: {
		description:
			"Cuatro estaciones marcadas. Veranos muy calurosos, inviernos fríos.",
		bestTime: "Marzo-abril (sakura) y noviembre (momiji/otoño).",
		avoid: "Agosto: calor extremo y humedad. Julio: lluvias.",
	},
};

const kiotoCityPrices = {
	cityName: "Kioto",
	currency: "€",
	accentColor: COLORS.kioto.accent,
	accentColorLight: COLORS.kioto.light,
	accommodation: {
		budget: { min: 20, max: 40, label: "Hostel / Guesthouse" },
		mid: { min: 50, max: 100, label: "Ryokan / Hotel 3-4★" },
		luxury: { min: 120, max: 350, label: "Ryokan de lujo" },
	},
	food: {
		budget: { min: 8, max: 15, label: "Ramen, udon, konbini" },
		mid: { min: 20, max: 40, label: "Restaurantes locales" },
		luxury: { min: 50, max: 120, label: "Kaiseki tradicional" },
	},
	transport: {
		budget: { min: 5, max: 8, label: "Bus diario con pase" },
		mid: { min: 8, max: 15, label: "Bus + tren" },
		luxury: { min: 20, max: 40, label: "Taxi" },
	},
	activities: {
		budget: { min: 5, max: 10, label: "Templos gratuitos, paseos" },
		mid: { min: 10, max: 25, label: "Entradas templos, kimono" },
		luxury: { min: 30, max: 70, label: "Ceremonia té, geishas" },
	},
	fixedCosts: {
		flight: { min: 500, max: 900, label: "Vuelo ida/vuelta a Japón" },
		insurance: { min: 25, max: 50, label: "Seguro de viaje" },
		sim: { min: 10, max: 25, label: "Pocket WiFi o eSIM" },
	},
	priceExamples: [
		{ item: "Ramen en local", price: "6-8€" },
		{ item: "Matcha latte", price: "3-4€" },
		{ item: "Bus diario pase", price: "4€" },
		{ item: "Entrada Kinkaku-ji", price: "3€" },
		{ item: "Alquiler kimono", price: "20-30€" },
		{ item: "Onigiri (konbini)", price: "1€" },
	],
};

const kiotoChecklistData = {
	cityId: "kioto",
	cityName: "Kioto",
	accentColor: COLORS.kioto.accent,
	accentColorLight: COLORS.kioto.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "passport", text: "Pasaporte vigente" },
				{ id: "insurance", text: "Seguro de viaje" },
				{ id: "copies", text: "Copias de documentos en la nube" },
				{ id: "jr-pass", text: "JR Pass (si vienes de Tokio)" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelos / tren bala reservado" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "kimono", text: "Alquiler kimono (opcional)" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "wifi", text: "Pocket WiFi o eSIM" },
				{ id: "apps", text: "Apps descargadas" },
				{ id: "offline", text: "Mapas offline" },
				{ id: "charger", text: "Cargador + adaptador" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{ id: "cash", text: "Yenes en efectivo" },
				{ id: "cards", text: "Tarjeta sin comisiones" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "shoes", text: "Zapatillas cómodas" },
				{ id: "socks", text: "Calcetines extra" },
				{ id: "modest", text: "Ropa modesta para templos" },
				{ id: "umbrella", text: "Paraguas plegable" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// ─────────────────────────────────────────────
// PARÍS
// ─────────────────────────────────────────────
const parisCityData = {
	cityId: "paris",
	cityName: "París",
	accentColor: COLORS.paris.accent,
	accentColorLight: COLORS.paris.light,
	documentation: {
		visa: "Con DNI o pasaporte español no necesitas visado. Si tu documento es de otro país, revisa si necesitas visado Schengen.",
		insurance:
			"Tarjeta Sanitaria Europea cubre urgencias. Seguro adicional recomendado.",
		vaccinations: "No hay vacunas obligatorias.",
	},
	packing: [
		{ icon: "👟", item: "Zapatos cómodos", note: "Mucho adoquín" },
		{ icon: "🧥", item: "Chaqueta ligera", note: "Puede refrescar" },
		{ icon: "☂️", item: "Paraguas pequeño", note: "Lluvias impredecibles" },
		{ icon: "👜", item: "Bolso/mochila segura", note: "Cuidado carteristas" },
		{ icon: "🔌", item: "Cargador USB", note: "Mismo enchufe que España" },
		{ icon: "📷", item: "Cámara de fotos", note: "Ciudad muy fotogénica" },
	],
	apps: [
		{ name: "Citymapper", description: "Mejor app para metro de París" },
		{ name: "Google Maps", description: "Navegación general" },
		{ name: "RATP", description: "App oficial metro/bus" },
		{ name: "TheFork", description: "Reservas restaurantes" },
	],
	connectivity: {
		sim: "Tu tarifa española funciona sin roaming (UE).",
		esim: "No necesitas. Roaming gratuito en la UE.",
		wifi: "WiFi en cafés, hoteles y algunas zonas públicas.",
	},
	money: {
		currency: "Euro (€)",
		exchangeRate: "Misma moneda que España.",
		cards: "Tarjeta aceptada en casi todos los sitios.",
		atm: "Cajeros con comisiones normales.",
		tip: "El servicio está incluido. Redondear está bien.",
	},
	language: {
		main: "Francés",
		english: "En zonas turísticas sí. Fuera, limitado.",
		phrases: [
			{ phrase: "Bonjour", meaning: "Hola / Buenos días" },
			{ phrase: "Merci beaucoup", meaning: "Muchas gracias" },
			{ phrase: "S'il vous plaît", meaning: "Por favor" },
			{ phrase: "L'addition", meaning: "La cuenta" },
		],
	},
	weather: {
		description:
			"Clima oceánico. Inviernos fríos (2-7°C), veranos agradables (20-25°C).",
		bestTime: "Abril-junio y septiembre-octubre.",
		avoid: "Agosto: muchos parisinos de vacaciones, calor.",
	},
};

const parisCityPrices = {
	cityName: "París",
	currency: "€",
	accentColor: COLORS.paris.accent,
	accentColorLight: COLORS.paris.light,
	accommodation: {
		budget: { min: 30, max: 60, label: "Hostel" },
		mid: { min: 80, max: 160, label: "Hotel 3-4 estrellas" },
		luxury: { min: 200, max: 500, label: "Hotel boutique / 5★" },
	},
	food: {
		budget: { min: 12, max: 20, label: "Boulangerie, crêpe, kebab" },
		mid: { min: 25, max: 45, label: "Bistrot / Brasserie" },
		luxury: { min: 60, max: 150, label: "Restaurante gastronómico" },
	},
	transport: {
		budget: { min: 3, max: 6, label: "Metro con Navigo/tickets" },
		mid: { min: 6, max: 12, label: "Metro + Uber puntual" },
		luxury: { min: 15, max: 35, label: "Uber/Taxi frecuente" },
	},
	activities: {
		budget: { min: 0, max: 10, label: "Paseos, museos gratis" },
		mid: { min: 15, max: 35, label: "Museos, crucero Sena" },
		luxury: { min: 40, max: 100, label: "Espectáculos, cenas" },
	},
	fixedCosts: {
		flight: { min: 40, max: 150, label: "Vuelo/tren desde España" },
		insurance: { min: 0, max: 20, label: "TSE + seguro opcional" },
		sim: { min: 0, max: 0, label: "Roaming UE incluido" },
	},
	priceExamples: [
		{ item: "Croissant", price: "1.20-1.50€" },
		{ item: "Café en terraza", price: "3-5€" },
		{ item: "Ticket metro", price: "2.15€" },
		{ item: "Entrada Louvre", price: "17€" },
		{ item: "Crêpe callejera", price: "4-6€" },
		{ item: "Copa de vino", price: "5-8€" },
	],
};

const parisChecklistData = {
	cityId: "paris",
	cityName: "París",
	accentColor: COLORS.paris.accent,
	accentColorLight: COLORS.paris.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "dni", text: "DNI o pasaporte vigente" },
				{ id: "tse", text: "Tarjeta Sanitaria Europea" },
				{ id: "insurance", text: "Seguro adicional (opcional)" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelo/tren reservado" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "louvre", text: "Entrada Louvre (se agota)" },
				{ id: "orsay", text: "Entrada Orsay (recomendado)" },
				{ id: "eiffel", text: "Entrada Torre Eiffel (reservar sí o sí)" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "apps", text: "Apps descargadas (Citymapper, RATP)" },
				{ id: "offline", text: "Mapas offline" },
				{ id: "charger", text: "Cargador (mismo enchufe)" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{ id: "cards", text: "Tarjeta de pago" },
				{ id: "cash", text: "Algo de efectivo" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "shoes", text: "Zapatos cómodos (adoquín)" },
				{ id: "jacket", text: "Chaqueta ligera" },
				{ id: "umbrella", text: "Paraguas pequeño" },
				{ id: "bag", text: "Bolso/mochila anti-robo" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// ─────────────────────────────────────────────
// LONDRES
// ─────────────────────────────────────────────
const londresCityData = {
	cityId: "londres",
	cityName: "Londres",
	accentColor: COLORS.londres.accent,
	accentColorLight: COLORS.londres.light,
	documentation: {
		visa: "Con pasaporte español necesitas ETA (se pide online). Si tu pasaporte es de otro país, revisa si necesitas ETA o visado.",
		insurance: "TSE ya no válida en UK. Seguro de viaje obligatorio.",
		vaccinations: "No hay vacunas obligatorias.",
	},
	packing: [
		{ icon: "🧥", item: "Chaqueta impermeable", note: "Llueve mucho" },
		{ icon: "👟", item: "Zapatos impermeables", note: "Suelo mojado" },
		{ icon: "☂️", item: "Paraguas compacto", note: "Imprescindible" },
		{
			icon: "🔌",
			item: "Adaptador tipo G",
			note: "Enchufe británico (3 clavijas)",
		},
		{ icon: "👕", item: "Ropa en capas", note: "Tiempo cambiante" },
		{ icon: "💳", item: "Tarjeta contactless", note: "Para el Tube" },
	],
	apps: [
		{ name: "Citymapper", description: "La mejor app para Londres" },
		{ name: "TfL Oyster", description: "App oficial transporte" },
		{ name: "Google Maps", description: "Navegación general" },
		{ name: "Too Good To Go", description: "Comida barata" },
	],
	connectivity: {
		sim: "Post-Brexit: roaming puede tener coste. Consulta tu operador.",
		esim: "eSIM recomendada (Airalo, Holafly) si tu operador cobra roaming.",
		wifi: "WiFi en cafés, museos y transporte.",
	},
	money: {
		currency: "Libra esterlina (£)",
		exchangeRate: "1€ ≈ 0.86£ (consulta antes de viajar)",
		cards:
			"Tarjeta contactless en casi todos lados. Apple/Google Pay muy común.",
		atm: "Cajeros gratuitos (busca 'free cash withdrawals').",
		tip: "10-15% en restaurantes si no está incluido.",
	},
	language: {
		main: "Inglés",
		english: "Idioma local. Sin problemas de comunicación.",
		phrases: [
			{ phrase: "Cheers!", meaning: "Gracias (informal)" },
			{ phrase: "Mind the gap", meaning: "Cuidado con el hueco (metro)" },
			{ phrase: "Queue", meaning: "Cola / Fila" },
			{ phrase: "Tube", meaning: "Metro de Londres" },
		],
	},
	weather: {
		description:
			"Oceánico. Lluvia frecuente. Verano suave (15-25°C), invierno frío (2-8°C).",
		bestTime: "Mayo-septiembre: más horas de luz y mejor tiempo.",
		avoid: "Noviembre-febrero: frío, oscuro y lluvioso.",
	},
};

const londresCityPrices = {
	cityName: "Londres",
	currency: "€",
	accentColor: COLORS.londres.accent,
	accentColorLight: COLORS.londres.light,
	accommodation: {
		budget: { min: 30, max: 60, label: "Hostel" },
		mid: { min: 80, max: 160, label: "Hotel 3-4 estrellas" },
		luxury: { min: 200, max: 500, label: "Hotel boutique / 5★" },
	},
	food: {
		budget: { min: 10, max: 18, label: "Pubs, mercados, takeaway" },
		mid: { min: 20, max: 40, label: "Restaurantes variados" },
		luxury: { min: 50, max: 120, label: "Restaurante premium" },
	},
	transport: {
		budget: { min: 5, max: 10, label: "Oyster/contactless off-peak" },
		mid: { min: 10, max: 18, label: "Tube + bus frecuente" },
		luxury: { min: 20, max: 40, label: "Uber/taxi" },
	},
	activities: {
		budget: { min: 0, max: 5, label: "Museos gratis, paseos" },
		mid: { min: 15, max: 35, label: "Entradas, tours, shows" },
		luxury: { min: 40, max: 100, label: "West End, experiencias" },
	},
	fixedCosts: {
		flight: { min: 30, max: 120, label: "Vuelo desde España" },
		insurance: { min: 15, max: 40, label: "Seguro viaje (obligatorio)" },
		sim: { min: 0, max: 15, label: "Roaming o eSIM" },
	},
	priceExamples: [
		{ item: "Fish & Chips", price: "8-12€" },
		{ item: "Pinta de cerveza", price: "6-8€" },
		{ item: "Billete Tube zona 1", price: "3€" },
		{ item: "Entrada Tower of London", price: "30€" },
		{ item: "Café para llevar", price: "3-4€" },
		{ item: "Sandwich M&S", price: "4-5€" },
	],
};

const londresChecklistData = {
	cityId: "londres",
	cityName: "Londres",
	accentColor: COLORS.londres.accent,
	accentColorLight: COLORS.londres.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "passport", text: "Pasaporte vigente (DNI no sirve)" },
				{ id: "insurance", text: "Seguro de viaje (TSE no válida)" },
				{ id: "copies", text: "Copias de documentos" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelos reservados" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "tower", text: "Entrada Tower of London" },
				{ id: "shows", text: "Entradas West End (si quieres)" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "sim", text: "Roaming UK o eSIM" },
				{ id: "apps", text: "Citymapper descargada" },
				{ id: "charger", text: "Adaptador enchufe tipo G" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{ id: "cards", text: "Tarjeta contactless (imprescindible)" },
				{ id: "notify-bank", text: "Avisar al banco del viaje" },
				{ id: "cash", text: "Algunas libras en efectivo" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "rain-jacket", text: "Chaqueta impermeable" },
				{ id: "shoes", text: "Zapatos impermeables" },
				{ id: "umbrella", text: "Paraguas compacto" },
				{ id: "layers", text: "Ropa en capas" },
				{ id: "adapter", text: "Adaptador tipo G" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// ─────────────────────────────────────────────
// NUEVA YORK
// ─────────────────────────────────────────────
const nyCityData = {
	cityId: "ny",
	cityName: "Nueva York",
	accentColor: COLORS.ny.accent,
	accentColorLight: COLORS.ny.light,
	documentation: {
		visa: "Con pasaporte español necesitas ESTA (se pide online). Si tu pasaporte es de otro país, revisa si necesitas ESTA o visado.",
		insurance: "Obligatorio. Sanidad en EEUU extremadamente cara.",
		vaccinations: "No hay vacunas obligatorias.",
	},
	packing: [
		{
			icon: "👟",
			item: "Zapatillas muy cómodas",
			note: "Caminarás 20.000+ pasos/día",
		},
		{
			icon: "🔌",
			item: "Adaptador tipo A/B",
			note: "Clavijas planas americanas",
		},
		{
			icon: "🧥",
			item: "Chaqueta según temporada",
			note: "Inviernos muy fríos",
		},
		{ icon: "👜", item: "Mochila o riñonera", note: "Día completo fuera" },
		{
			icon: "💧",
			item: "Botella de agua reutilizable",
			note: "Fuentes en parques",
		},
		{ icon: "🔋", item: "Power bank", note: "GPS todo el día" },
	],
	apps: [
		{ name: "Citymapper", description: "Metro NYC perfecto" },
		{ name: "Google Maps", description: "Navegación general" },
		{ name: "Uber/Lyft", description: "Alternativa al taxi" },
		{ name: "OpenTable", description: "Reservas restaurantes" },
		{ name: "NYC Ferry", description: "Horarios ferries" },
	],
	connectivity: {
		sim: "SIM prepago en tiendas. Desde 30$ con datos ilimitados.",
		esim: "eSIM con Holafly, Airalo. Muy recomendable.",
		wifi: "WiFi en cafés, hoteles y algunas zonas públicas.",
	},
	money: {
		currency: "Dólar estadounidense (USD)",
		exchangeRate: "1€ ≈ 1.08$ (consulta antes de viajar)",
		cards: "Tarjeta aceptada en todos lados. Apple/Google Pay común.",
		atm: "Cajeros cobran 3-5$ comisión. Usa tu tarjeta directamente.",
		tip: "Propina obligatoria: 18-20% en restaurantes, 1-2$ por bebida.",
	},
	language: {
		main: "Inglés",
		english: "Idioma local. Ciudad muy multicultural.",
		phrases: [
			{ phrase: "How are you?", meaning: "¿Qué tal? (saludo obligado)" },
			{ phrase: "Check, please", meaning: "La cuenta, por favor" },
			{ phrase: "Tip", meaning: "Propina (obligatoria)" },
			{ phrase: "Subway", meaning: "Metro" },
		],
	},
	weather: {
		description:
			"Continental. Inviernos muy fríos (-5 a 5°C), veranos calurosos (28-35°C).",
		bestTime: "Abril-junio y septiembre-noviembre.",
		avoid: "Enero-febrero: frío extremo. Agosto: calor húmedo.",
	},
};

const nyCityPrices = {
	cityName: "Nueva York",
	currency: "€",
	accentColor: COLORS.ny.accent,
	accentColorLight: COLORS.ny.light,
	accommodation: {
		budget: { min: 40, max: 80, label: "Hostel / Airbnb compartido" },
		mid: { min: 120, max: 220, label: "Hotel 3-4 estrellas" },
		luxury: { min: 300, max: 700, label: "Hotel boutique / 5★" },
	},
	food: {
		budget: { min: 15, max: 25, label: "Pizza, bagels, food trucks" },
		mid: { min: 30, max: 55, label: "Restaurantes con propina" },
		luxury: { min: 70, max: 200, label: "Fine dining" },
	},
	transport: {
		budget: { min: 5, max: 8, label: "MetroCard ilimitada" },
		mid: { min: 8, max: 15, label: "Metro + Uber puntual" },
		luxury: { min: 20, max: 50, label: "Uber/taxi frecuente" },
	},
	activities: {
		budget: { min: 0, max: 10, label: "Parques, paseos, museos gratis" },
		mid: { min: 20, max: 50, label: "Top of the Rock, museos" },
		luxury: { min: 50, max: 120, label: "Broadway, helicóptero" },
	},
	fixedCosts: {
		flight: { min: 300, max: 600, label: "Vuelo ida/vuelta" },
		insurance: { min: 30, max: 60, label: "Seguro viaje EEUU" },
		sim: { min: 10, max: 30, label: "eSIM o SIM local" },
	},
	priceExamples: [
		{ item: "Slice de pizza", price: "2-4€" },
		{ item: "Café Starbucks", price: "5-6€" },
		{ item: "MetroCard viaje", price: "2.50€" },
		{ item: "Top of the Rock", price: "35€" },
		{ item: "Entrada Broadway", price: "80-150€" },
		{ item: "Bagel con cream cheese", price: "3-5€" },
		{ item: "Cerveza en bar", price: "7-10€" },
	],
};

const nyChecklistData = {
	cityId: "ny",
	cityName: "Nueva York",
	accentColor: COLORS.ny.accent,
	accentColorLight: COLORS.ny.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "passport", text: "Pasaporte vigente" },
				{ id: "esta", text: "ESTA aprobado (mínimo 72h antes)" },
				{ id: "insurance", text: "Seguro de viaje EEUU (imprescindible)" },
				{ id: "copies", text: "Copias de documentos" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelos reservados" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "statue", text: "Entrada Estatua Libertad (se agota)" },
				{ id: "broadway", text: "Entradas Broadway" },
				{ id: "observatories", text: "Entrada observatorio" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "sim", text: "eSIM o SIM americana" },
				{ id: "apps", text: "Citymapper descargada" },
				{ id: "charger", text: "Adaptador tipo A/B" },
				{ id: "powerbank", text: "Power bank" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{ id: "cards", text: "Tarjeta sin comisiones internacionales" },
				{ id: "cash", text: "Algunos dólares en efectivo" },
				{ id: "notify-bank", text: "Avisar al banco del viaje" },
				{ id: "tip-ready", text: "Preparado para propinas (18-20%)" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "shoes", text: "Zapatillas MUY cómodas" },
				{ id: "adapter", text: "Adaptador enchufe A/B" },
				{ id: "layers", text: "Ropa según temporada" },
				{ id: "bottle", text: "Botella agua reutilizable" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// ─────────────────────────────────────────────
// SEÚL
// ─────────────────────────────────────────────
const seulCityData = {
	cityId: "seul",
	cityName: "Seúl",
	accentColor: COLORS.seul.accent,
	accentColorLight: COLORS.seul.light,
	documentation: {
		visa: "Con pasaporte español puedes entrar en Corea del Sur sin visado hasta 90 días. Actualmente no necesitas K-ETA, aunque esta exención es temporal, así que conviene comprobar los requisitos antes de viajar. Sí debes completar la e-Arrival Card antes de llegar. Si tienes otra nacionalidad, revisa los requisitos específicos.",
		insurance: "Recomendado. Sanidad buena pero cara.",
		vaccinations: "No hay vacunas obligatorias.",
	},
	packing: [
		{ icon: "👟", item: "Zapatillas cómodas", note: "Mucho caminar" },
		{
			icon: "🔌",
			item: "Adaptador tipo C/F",
			note: "Mismo que España (generalmente)",
		},
		{ icon: "🧥", item: "Ropa según temporada", note: "Inviernos muy fríos" },
		{ icon: "🧴", item: "Espacio para skincare", note: "Comprarás cosmética" },
		{ icon: "☂️", item: "Paraguas", note: "Verano lluvioso" },
		{
			icon: "👕",
			item: "Ropa modesta para palacios",
			note: "Hombros cubiertos",
		},
	],
	apps: [
		{ name: "Naver Map", description: "Mejor que Google Maps en Corea" },
		{ name: "KakaoMap", description: "Alternativa local a Maps" },
		{ name: "Papago", description: "Traductor coreano" },
		{ name: "T-money", description: "Tarjeta transporte" },
		{ name: "MangoPlate", description: "Restaurantes recomendados" },
	],
	connectivity: {
		sim: "SIM/eSIM en aeropuerto Incheon. Desde 15.000₩ (~10€).",
		esim: "eSIM con Airalo o Holafly. Muy recomendable.",
		wifi: "WiFi gratuito en metro, cafés y espacios públicos. Excelente cobertura.",
	},
	money: {
		currency: "Won surcoreano (₩)",
		exchangeRate: "1€ ≈ 1.450₩ (consulta antes de viajar)",
		cards: "Tarjeta aceptada en casi todos lados. T-money para transporte.",
		atm: "Cajeros internacionales en konbini.",
		tip: "En Corea no se deja propina. La tarjeta está muy extendida, pero conviene llevar algo de efectivo, especialmente para recargar una T-money, ya que algunas máquinas todavía solo aceptan efectivo.",
	},
	language: {
		main: "Coreano",
		english: "Limitado fuera de zonas turísticas. Señalización bilingüe.",
		phrases: [
			{ phrase: "Annyeonghaseyo", meaning: "Hola" },
			{ phrase: "Kamsahamnida", meaning: "Gracias" },
			{ phrase: "Eolmayeyo?", meaning: "¿Cuánto cuesta?" },
			{ phrase: "Mashisseoyo!", meaning: "¡Está delicioso!" },
		],
	},
	weather: {
		description:
			"Continental. Inviernos muy fríos (-10 a 2°C), veranos calurosos (25-35°C).",
		bestTime: "Marzo-mayo (primavera, cerezos) y septiembre-noviembre (otoño).",
		avoid: "Julio-agosto: monzón, calor húmedo extremo.",
	},
};

const seulCityPrices = {
	cityName: "Seúl",
	currency: "€",
	accentColor: COLORS.seul.accent,
	accentColorLight: COLORS.seul.light,
	accommodation: {
		budget: { min: 15, max: 35, label: "Hostel / Guesthouse" },
		mid: { min: 40, max: 90, label: "Hotel 3-4 estrellas" },
		luxury: { min: 100, max: 300, label: "Hotel 5 estrellas" },
	},
	food: {
		budget: { min: 5, max: 12, label: "Street food, konbini, kimbap" },
		mid: { min: 12, max: 25, label: "BBQ coreano, restaurantes" },
		luxury: { min: 30, max: 80, label: "Restaurante premium" },
	},
	transport: {
		budget: { min: 3, max: 5, label: "Metro con T-money" },
		mid: { min: 5, max: 12, label: "Metro + bus + taxi puntual" },
		luxury: { min: 15, max: 30, label: "Taxi frecuente" },
	},
	activities: {
		budget: { min: 0, max: 5, label: "Palacios, paseos, mercados" },
		mid: { min: 10, max: 25, label: "K-pop, hanbok, tours" },
		luxury: { min: 30, max: 70, label: "Shows, experiencias VIP" },
	},
	fixedCosts: {
		flight: { min: 400, max: 800, label: "Vuelo ida/vuelta" },
		insurance: { min: 20, max: 50, label: "Seguro de viaje" },
		sim: { min: 10, max: 20, label: "eSIM o SIM local" },
	},
	priceExamples: [
		{ item: "Kimbap rollo", price: "2-3€" },
		{ item: "BBQ coreano (por persona)", price: "10-15€" },
		{ item: "Billete metro", price: "0.80€" },
		{ item: "Café en Gangnam", price: "4-5€" },
		{ item: "Entrada palacio", price: "2€" },
		{ item: "Tteokbokki callejero", price: "2-3€" },
	],
};

const seulChecklistData = {
	cityId: "seul",
	cityName: "Seúl",
	accentColor: COLORS.seul.accent,
	accentColorLight: COLORS.seul.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "passport", text: "Pasaporte vigente" },
				{ id: "keta", text: "e-Arrival Card completada" },
				{ id: "insurance", text: "Seguro de viaje" },
				{ id: "copies", text: "Copias de documentos" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelos reservados" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "hanbok", text: "Alquiler hanbok (opcional)" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "sim", text: "eSIM o SIM coreana" },
				{ id: "apps", text: "Naver Map y Papago descargados" },
				{ id: "charger", text: "Cargador (enchufe tipo C/F)" },
				{ id: "tmoney", text: "T-money card (comprar en aeropuerto)" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{ id: "cards", text: "Tarjeta sin comisiones" },
				{ id: "cash", text: "Algunos wones para mercados" },
				{ id: "notify-bank", text: "Avisar al banco" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "shoes", text: "Zapatillas cómodas" },
				{ id: "skincare-space", text: "Espacio para compras (cosmética)" },
				{ id: "layers", text: "Ropa según temporada" },
				{ id: "umbrella", text: "Paraguas" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// ─────────────────────────────────────────────
// SAN FRANCISCO
// ─────────────────────────────────────────────
const sanFranciscoCityData = {
	cityId: "sanFrancisco",
	cityName: "San Francisco",
	accentColor: COLORS.sanFrancisco.accent,
	accentColorLight: COLORS.sanFrancisco.light,
	documentation: {
		visa: "Con pasaporte español necesitas ESTA (se pide online). Si tu pasaporte es de otro país, revisa si necesitas ESTA o visado.",
		insurance: "Obligatorio. Sanidad EEUU extremadamente cara.",
		vaccinations: "No hay vacunas obligatorias.",
	},
	packing: [
		{ icon: "🧥", item: "Chaqueta abrigada", note: "La niebla enfría mucho" },
		{ icon: "👟", item: "Zapatillas cómodas", note: "Ciudad con cuestas" },
		{ icon: "🔌", item: "Adaptador tipo A/B", note: "Clavijas americanas" },
		{ icon: "🧣", item: "Bufanda ligera", note: "Viento del Pacífico" },
		{ icon: "👕", item: "Ropa en capas", note: "Microclimas cambiantes" },
		{ icon: "🔋", item: "Power bank", note: "GPS todo el día" },
	],
	apps: [
		{ name: "Clipper Card", description: "Transporte público SF" },
		{ name: "Uber/Lyft", description: "Alternativa al transporte" },
		{ name: "Google Maps", description: "Navegación" },
		{ name: "Yelp", description: "Restaurantes y reseñas" },
	],
	connectivity: {
		sim: "SIM prepago desde 30$. Mejor en tienda de operador.",
		esim: "eSIM con Holafly o Airalo. Recomendable.",
		wifi: "WiFi en cafés y hoteles.",
	},
	money: {
		currency: "Dólar estadounidense (USD)",
		exchangeRate: "1€ ≈ 1.08$ (consulta antes de viajar)",
		cards: "Tarjeta en todos lados. Apple/Google Pay común.",
		atm: "Cajeros con comisión 3-5$.",
		tip: "Propina obligatoria: 18-20% en restaurantes.",
	},
	language: {
		main: "Inglés",
		english: "Idioma local. Ciudad multicultural.",
		phrases: [
			{ phrase: "SF / The City", meaning: "Nunca digas 'San Fran' o 'Frisco'" },
			{ phrase: "The Bay", meaning: "Área de la Bahía" },
			{ phrase: "BART", meaning: "Metro/tren regional" },
			{ phrase: "Fog", meaning: "La niebla (personaje famoso)" },
		],
	},
	weather: {
		description:
			"Microclimas. Niebla y fresco incluso en verano. 10-18°C es habitual.",
		bestTime: "Septiembre-octubre: 'verano indio', mejor clima del año.",
		avoid: "Junio-agosto: paradójicamente, puede ser frío y con niebla.",
	},
};

const sanFranciscoCityPrices = {
	cityName: "San Francisco",
	currency: "€",
	accentColor: COLORS.sanFrancisco.accent,
	accentColorLight: COLORS.sanFrancisco.light,
	accommodation: {
		budget: { min: 40, max: 80, label: "Hostel" },
		mid: { min: 120, max: 250, label: "Hotel 3-4 estrellas" },
		luxury: { min: 300, max: 700, label: "Hotel boutique / 5★" },
	},
	food: {
		budget: { min: 12, max: 20, label: "Food trucks, burrito, ramen" },
		mid: { min: 25, max: 50, label: "Restaurantes con propina" },
		luxury: { min: 60, max: 200, label: "Fine dining" },
	},
	transport: {
		budget: { min: 5, max: 10, label: "Muni/BART con Clipper" },
		mid: { min: 10, max: 20, label: "Bus + Uber puntual" },
		luxury: { min: 25, max: 50, label: "Uber/taxi frecuente" },
	},
	activities: {
		budget: { min: 0, max: 10, label: "Golden Gate, paseos, parques" },
		mid: { min: 15, max: 35, label: "Alcatraz, museos" },
		luxury: { min: 40, max: 100, label: "Tours vino Napa, kayak" },
	},
	fixedCosts: {
		flight: { min: 350, max: 700, label: "Vuelo ida/vuelta" },
		insurance: { min: 30, max: 60, label: "Seguro viaje EEUU" },
		sim: { min: 10, max: 30, label: "eSIM o SIM local" },
	},
	priceExamples: [
		{ item: "Mission burrito", price: "10-12€" },
		{ item: "Clam chowder (Fisherman's)", price: "8-12€" },
		{ item: "Billete Muni", price: "2.50€" },
		{ item: "Entrada Alcatraz", price: "40€" },
		{ item: "Café artesanal", price: "5-6€" },
		{ item: "Cable Car ride", price: "7€" },
	],
};

const sanFranciscoChecklistData = {
	cityId: "sanFrancisco",
	cityName: "San Francisco",
	accentColor: COLORS.sanFrancisco.accent,
	accentColorLight: COLORS.sanFrancisco.light,
	successColor: "#28a745",
	categories: [
		{
			id: "docs",
			title: "Documentación",
			items: [
				{ id: "passport", text: "Pasaporte vigente" },
				{ id: "esta", text: "ESTA aprobado" },
				{ id: "insurance", text: "Seguro de viaje EEUU" },
				{ id: "copies", text: "Copias de documentos" },
			],
		},
		{
			id: "bookings",
			title: "Reservas",
			items: [
				{ id: "flights", text: "Vuelos reservados" },
				{ id: "hotel", text: "Alojamiento reservado" },
				{ id: "alcatraz", text: "Entrada Alcatraz (reservar con semanas)" },
			],
		},
		{
			id: "tech",
			title: "Tecnología",
			items: [
				{ id: "sim", text: "eSIM o SIM americana" },
				{ id: "apps", text: "Clipper Card app" },
				{ id: "charger", text: "Adaptador tipo A/B" },
				{ id: "powerbank", text: "Power bank" },
			],
		},
		{
			id: "money",
			title: "Dinero",
			items: [
				{ id: "cards", text: "Tarjeta sin comisiones" },
				{ id: "cash", text: "Algunos dólares" },
				{ id: "notify-bank", text: "Avisar al banco" },
			],
		},
		{
			id: "packing",
			title: "Maleta",
			items: [
				{ id: "jacket", text: "Chaqueta abrigada (niebla)" },
				{ id: "shoes", text: "Zapatillas cómodas (cuestas)" },
				{ id: "layers", text: "Ropa en capas" },
				{ id: "scarf", text: "Bufanda ligera" },
				{ id: "meds", text: "Botiquín básico" },
			],
		},
		{
			id: "lastmin",
			title: "Último momento",
			items: [
				{ id: "checkin", text: "Check-in online" },
				{ id: "boarding", text: "Tarjetas de embarque" },
				{ id: "house", text: "Casa preparada" },
			],
		},
	],
};

// Destinos disponibles para el selector (orden visual)
export const AVAILABLE_DESTINATIONS = [
	{ id: "tokio", label: "Tokio" },
	{ id: "kioto", label: "Kioto" },
	{ id: "paris", label: "París" },
	{ id: "londres", label: "Londres" },
	{ id: "ny", label: "Nueva York" },
	{ id: "seul", label: "Seúl" },
	{ id: "sanFrancisco", label: "San Francisco" },
];

// ─────────────────────────────────────────────
// MAPA PRINCIPAL: guideId → datos de módulos
// ─────────────────────────────────────────────
const cityModulesMap = {
	tokio: {
		cityData: tokioCityData,
		cityPrices: tokioCityPrices,
		checklistData: tokioChecklistData,
	},
	kioto: {
		cityData: kiotoCityData,
		cityPrices: kiotoCityPrices,
		checklistData: kiotoChecklistData,
	},
	paris: {
		cityData: parisCityData,
		cityPrices: parisCityPrices,
		checklistData: parisChecklistData,
	},
	londres: {
		cityData: londresCityData,
		cityPrices: londresCityPrices,
		checklistData: londresChecklistData,
	},
	ny: {
		cityData: nyCityData,
		cityPrices: nyCityPrices,
		checklistData: nyChecklistData,
	},
	seul: {
		cityData: seulCityData,
		cityPrices: seulCityPrices,
		checklistData: seulChecklistData,
	},
	sanFrancisco: {
		cityData: sanFranciscoCityData,
		cityPrices: sanFranciscoCityPrices,
		checklistData: sanFranciscoChecklistData,
	},
};

/**
 * Devuelve los datos de módulos para un guideId.
 * Si no se encuentra, crea unos genéricos con el nombre de la guía.
 *
 * @param {string} guideId - ID de la guía (tokio, kioto, paris, etc.)
 * @param {string} guideName - Nombre de la guía (fallback si no hay datos)
 * @returns {{ cityData, cityPrices, checklistData }}
 */
export function getModuleDataForCity(guideId, guideName = "Mi viaje") {
	// Normalizar ID: quitar acentos, minúsculas, posibles variaciones
	const normalizedId = (guideId || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");

	// Buscar directamente
	if (cityModulesMap[normalizedId]) {
		return cityModulesMap[normalizedId];
	}

	// Buscar parcialmente (por si el id contiene el nombre de la ciudad)
	for (const [key, data] of Object.entries(cityModulesMap)) {
		if (normalizedId.includes(key) || key.includes(normalizedId)) {
			return data;
		}
	}

	// Fallback genérico con el nombre de la guía
	const ACCENT = "#6366F1";
	const ACCENT_LIGHT = "rgba(99, 102, 241, 0.12)";

	return {
		cityData: {
			cityName: guideName,
			accentColor: ACCENT,
			accentColorLight: ACCENT_LIGHT,
			documentation: {
				visa: "Consulta los requisitos de visado para tu destino.",
				insurance: "Recomendamos siempre contratar un seguro de viaje.",
				vaccinations: "Consulta las vacunas recomendadas para tu destino.",
			},
			packing: [
				{ icon: "👟", item: "Zapatos cómodos", note: "Para caminar" },
				{ icon: "🔌", item: "Adaptador de enchufe", note: "Consulta el tipo" },
				{ icon: "☂️", item: "Paraguas", note: "Por si acaso" },
				{
					icon: "💊",
					item: "Botiquín básico",
					note: "Medicamentos habituales",
				},
			],
			apps: [
				{ name: "Google Maps", description: "Navegación" },
				{ name: "Google Translate", description: "Traductor" },
			],
			connectivity: {
				sim: "Consulta opciones de SIM local en tu destino.",
				esim: "eSIM con Airalo o Holafly puede ser buena opción.",
				wifi: "WiFi disponible en hoteles y cafés.",
			},
			money: {
				currency: "Consulta la moneda local",
				exchangeRate: "Consulta el cambio antes de viajar",
				cards: "Lleva tarjeta y algo de efectivo.",
				atm: "Busca cajeros sin comisión.",
				tip: "Consulta la costumbre local de propinas.",
			},
			language: {
				main: "Consulta el idioma local",
				english: "Varía según el destino.",
				phrases: [],
			},
			weather: {
				description: "Consulta el clima de tu destino.",
				bestTime: "Consulta la mejor época para viajar.",
				avoid: "Consulta qué épocas evitar.",
			},
		},
		cityPrices: {
			cityName: guideName,
			currency: "€",
			accentColor: ACCENT,
			accentColorLight: ACCENT_LIGHT,
			accommodation: {
				budget: { min: 20, max: 50, label: "Hostel" },
				mid: { min: 60, max: 120, label: "Hotel 3-4★" },
				luxury: { min: 150, max: 400, label: "Hotel 5★" },
			},
			food: {
				budget: { min: 10, max: 20, label: "Comida local" },
				mid: { min: 20, max: 40, label: "Restaurantes" },
				luxury: { min: 50, max: 120, label: "Premium" },
			},
			transport: {
				budget: { min: 3, max: 8, label: "Transporte público" },
				mid: { min: 8, max: 15, label: "Mixto" },
				luxury: { min: 20, max: 40, label: "Taxi/privado" },
			},
			activities: {
				budget: { min: 0, max: 10, label: "Gratuitas" },
				mid: { min: 10, max: 30, label: "Entradas, tours" },
				luxury: { min: 30, max: 80, label: "Experiencias" },
			},
			fixedCosts: {
				flight: { min: 100, max: 500, label: "Vuelo (aprox)" },
				insurance: { min: 15, max: 50, label: "Seguro" },
				sim: { min: 5, max: 20, label: "SIM/eSIM" },
			},
			priceExamples: [],
		},
		checklistData: {
			cityId: guideId || "generic",
			cityName: guideName,
			accentColor: ACCENT,
			accentColorLight: ACCENT_LIGHT,
			successColor: "#28a745",
			categories: [
				{
					id: "docs",
					title: "Documentación",
					items: [
						{ id: "passport", text: "Pasaporte/DNI vigente" },
						{ id: "insurance", text: "Seguro de viaje" },
						{ id: "copies", text: "Copias de documentos" },
					],
				},
				{
					id: "bookings",
					title: "Reservas",
					items: [
						{ id: "flights", text: "Vuelos/transporte reservado" },
						{ id: "hotel", text: "Alojamiento reservado" },
					],
				},
				{
					id: "tech",
					title: "Tecnología",
					items: [
						{ id: "sim", text: "SIM/eSIM para el destino" },
						{ id: "apps", text: "Apps útiles descargadas" },
						{ id: "charger", text: "Cargador y adaptador" },
					],
				},
				{
					id: "money",
					title: "Dinero",
					items: [
						{ id: "cards", text: "Tarjeta de pago" },
						{ id: "cash", text: "Efectivo local" },
					],
				},
				{
					id: "packing",
					title: "Maleta",
					items: [
						{ id: "shoes", text: "Zapatos cómodos" },
						{ id: "weather-clothes", text: "Ropa según clima" },
						{ id: "meds", text: "Botiquín básico" },
					],
				},
				{
					id: "lastmin",
					title: "Último momento",
					items: [
						{ id: "checkin", text: "Check-in online" },
						{ id: "boarding", text: "Tarjetas de embarque" },
						{ id: "house", text: "Casa preparada" },
					],
				},
			],
		},
	};
}

/** Obtiene datos de módulos por ID de destino (para el selector manual) */
export function getModuleDataByDestinationId(destId) {
	return cityModulesMap[destId] || null;
}

export default cityModulesMap;
